import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { ProductionShiftService } from 'src/app/services/production-shift/production-shift.service';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { AlertService, MessageSeverity } from 'src/app/shared/services/alert.service';

@Component({
  selector: 'app-production-shift-form-modal',
  templateUrl: './production-shift-form-modal.component.html',
  styleUrls: ['./production-shift-form-modal.component.css']
})
export class ProductionShiftFormModalComponent implements OnInit {
  
  newRecord = {
    id: 0,
    idActuator: 0,
    idShift: 1,
    projectedQuantity: 0,
    qualityQuantity: 0,
    weftDensityPpcm: 0 // Nuevo campo
  };

  // Paradas programadas
  scheduledStops: any[] = [];
  editingStopIndex: number = -1;
  isNewRecord: boolean = true;
  
  // Campos para nueva parada
  newStopName: string = '';
  newStopDuration: number = 0;
  showAddStopForm: boolean = false;
  
  // Para edición inline
  editingStop: any = {};

  constructor(
    public dialogRef: MatDialogRef<ProductionShiftFormModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private productionShiftService: ProductionShiftService,
    private spinner: NgxSpinnerService,
    private alertService: AlertService
  ) { }

  ngOnInit() {
    if (this.data.record) {
      // Modo edición
      this.isNewRecord = false;
      this.newRecord = {
        id: this.data.record.id,
        idActuator: this.data.record.idActuator || 0,
        idShift: this.data.record.idShift || 0,
        projectedQuantity: this.data.record.projectedQuantity || 0,
        qualityQuantity: this.data.record.qualityQuantity || 0,
        weftDensityPpcm: this.data.record.weftDensityPpcm || 0 // Nuevo campo
      };
      
      // Cargar paradas programadas del registro
      this.loadStopList(this.data.record.id);
    } else {
      // Modo creación
      this.isNewRecord = true;
      this.newRecord.idActuator = this.data.idActuator || 0;
      this.loadStopScheduleList();
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (!this.validateForm()) {
      return;
    }

    this.spinner.show();
    const recordData = {
      id: this.newRecord.id ? this.newRecord.id : 0,
      idEnterprise: localStorage.getItem(AppConstants.Session.ENTERPRISE),
      idHeadquarter: localStorage.getItem(AppConstants.Session.HEADQUARTER),
      idActuator: this.newRecord.idActuator,
      idShift: this.newRecord.idShift,
      projectedQuantity: this.newRecord.projectedQuantity,
      qualityQuantity: this.newRecord.qualityQuantity,
      weftDensityPpcm: this.newRecord.weftDensityPpcm, // Nuevo campo
      scheduledStops: this.scheduledStops
    };

    const serviceCall = this.isNewRecord 
      ? this.productionShiftService.create(recordData)
      : this.productionShiftService.update(recordData);

    serviceCall.subscribe(
      (response) => {
        if (response.code == 200) {
          this.alertService.showMessage(
            this.isNewRecord ? 'Registro creado correctamente' : 'Registro actualizado correctamente',
            '',
            MessageSeverity.success
          );
          this.dialogRef.close({ success: true, data: response.data });
        } else {
          this.alertService.showMessage(response.message, '', MessageSeverity.error);
        }
        this.spinner.hide();
      },
      (error) => {
        this.alertService.showMessage('Error al guardar el registro', '', MessageSeverity.error);
        this.spinner.hide();
      }
    );
  }

  validateForm(): boolean {
    if (this.newRecord.idActuator === null || this.newRecord.idActuator === undefined || this.newRecord.idActuator === 0) {
      this.alertService.showMessage('El actuador es requerido', '', MessageSeverity.error);
      return false;
    }
    
    if (this.newRecord.idShift === null || this.newRecord.idShift === undefined) {
      this.alertService.showMessage('El turno es requerido', '', MessageSeverity.error);
      return false;
    }
    
    if (this.newRecord.projectedQuantity < 0) {
      this.alertService.showMessage('Los metros proyectados deben ser un valor positivo', '', MessageSeverity.error);
      return false;
    }
    
    if (this.newRecord.weftDensityPpcm < 0) {
      this.alertService.showMessage('La densidad de trama por cm debe ser un valor positivo', '', MessageSeverity.error);
      return false;
    }
    
    // Solo validar qualityQuantity si se está editando un registro existente
    if (!this.isNewRecord && this.newRecord.qualityQuantity < 0) {
      this.alertService.showMessage('Los metros de calidad deben ser un valor positivo', '', MessageSeverity.error);
      return false;
    }
    return true;
  }

  // Métodos para manejar paradas programadas (copiados del componente original)
  addNewStop() {
    if (!this.newStopName || !this.newStopDuration) {
      this.alertService.showMessage('Por favor complete el nombre y la duración', '', MessageSeverity.error);
      return;
    }

    if (!this.isNewRecord) {
      // Si se está editando un registro existente, crear la parada directamente en el API
      const stopData = {
        idProductionShift: this.newRecord.id,
        name: this.newStopName,
        durationMinutes: this.newStopDuration
      };

      this.productionShiftService.createStop(stopData).subscribe(
        (response) => {
          if (response.error == null) {
            // Recargar la lista de paradas después de crear
            this.loadStopList(this.newRecord.id);
            this.alertService.showMessage('Parada agregada correctamente', '', MessageSeverity.success);
          } else {
            this.alertService.showMessage(response.error.message, '', MessageSeverity.error);
          }
        },
        (error) => {
          this.alertService.showMessage('Error al agregar la parada', '', MessageSeverity.error);
        }
      );
    } else {
      // Para nuevos registros, agregar temporalmente al array local
      const newStop = {
        id: 0,
        name: this.newStopName,
        durationMinutes: this.newStopDuration
      };
      this.scheduledStops.push(newStop);
    }
    
    // Limpiar campos y ocultar formulario
    this.cancelAddStop();
  }

  cancelAddStop() {
    this.newStopName = '';
    this.newStopDuration = 0;
    this.showAddStopForm = false;
  }

  startEditStop(index: number) {
    this.editingStopIndex = index;
    this.editingStop = { ...this.scheduledStops[index] };
  }

  saveEditStop(index: number) {
    if (!this.editingStop.name || !this.editingStop.durationMinutes) {
      this.alertService.showMessage('Por favor complete el nombre y la duración', '', MessageSeverity.error);
      return;
    }

    if (!this.isNewRecord && this.editingStop.id > 0) {
      // Si se está editando un registro existente y la parada tiene ID, actualizar en el API
      const stopData = {
        id: this.editingStop.id,
        name: this.editingStop.name,
        durationMinutes: this.editingStop.durationMinutes
      };

      this.productionShiftService.updateStop(stopData).subscribe(
        (response) => {
          if (response.error == null) {
            // Recargar la lista de paradas después de actualizar
            this.loadStopList(this.newRecord.id);
            this.alertService.showMessage('Parada actualizada correctamente', '', MessageSeverity.success);
          } else {
            this.alertService.showMessage(response.error.message, '', MessageSeverity.error);
          }
        },
        (error) => {
          this.alertService.showMessage('Error al actualizar la parada', '', MessageSeverity.error);
        }
      );
    } else {
      // Para nuevos registros o paradas temporales, actualizar en el array local
      this.scheduledStops[index] = { ...this.editingStop };
    }
    
    this.cancelEditStop();
  }

  cancelEditStop() {
    this.editingStopIndex = -1;
    this.editingStop = {};
  }

  deleteScheduledStop(index: number) {
    const stop = this.scheduledStops[index];
    
    if (confirm(`¿Estás seguro que deseas eliminar la parada "${stop.name}"? Esta acción no se puede deshacer.`)) {
      if (!this.isNewRecord && stop.id > 0) {
        // Si se está editando un registro existente y la parada tiene ID, eliminar del API
        this.productionShiftService.deleteStop(stop.id).subscribe(
          (response) => {
            if (response.code == 200) {
              // Recargar la lista de paradas después de eliminar
              this.loadStopList(this.newRecord.id);
              this.alertService.showMessage('Parada eliminada correctamente', '', MessageSeverity.success);
            } else {
              this.alertService.showMessage(response.message, '', MessageSeverity.error);
            }
          },
          (error) => {
            this.alertService.showMessage('Error al eliminar la parada', '', MessageSeverity.error);
          }
        );
      } else {
        // Para nuevos registros o paradas temporales, eliminar del array local
        this.scheduledStops.splice(index, 1);
        this.alertService.showMessage('Parada eliminada correctamente', '', MessageSeverity.success);
      }
      
      this.editingStopIndex = -1;
    }
  }

  // Cargar lista de paradas programadas para nuevos registros
  loadStopScheduleList() {
    const idEnterprise = localStorage.getItem(AppConstants.Session.ENTERPRISE);
    if (idEnterprise) {
      this.productionShiftService.getStopScheduleList(idEnterprise).subscribe(
        (response) => {
          if (response.error == null) {
            this.scheduledStops = response.data || [];
          } else {
            this.alertService.showMessage(response.error.message, '', MessageSeverity.error);
          }
        },
        (error) => {
          this.alertService.showMessage('Error al cargar las paradas programadas', '', MessageSeverity.error);
        }
      );
    }
  }

  // Cargar lista de paradas para registros existentes
  loadStopList(recordId: number) {
    this.productionShiftService.getStopList(recordId).subscribe(
      (response) => {
        if (response.error == null) {
          this.scheduledStops = response.data || [];
        } else {
          this.alertService.showMessage(response.error.message, '', MessageSeverity.error);
        }
      },
      (error) => {
        this.alertService.showMessage('Error al cargar las paradas del registro', '', MessageSeverity.error);
      }
    );
  }
}
